import lodashGet from "lodash/get";
import React from "react";
import { ColumnTypes } from "shared/components/pillar-table/custom-reports/ColumnTypesMapper";
// `import type` keeps this module free of a runtime dependency on the
// component, which imports these helpers in turn.
import type { PillarTableNestedTableProps } from "shared/components/pillar-table/nested/PillarTableNestedTable";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";

export type PillarTableNestedTableElement =
  React.ReactElement<PillarTableNestedTableProps>;

/**
 * A nested table reaches us two ways: written in JSX as
 * `<PillarTable.NestedTable>`, or rendered from a custom report column config,
 * which only carries `columnType`. Every caller has to recognise both.
 *
 * The JSX form is matched on the component's own `isNestedTable` flag rather
 * than on component identity, so that this module stays importable from
 * `PillarTableNestedTable` itself.
 */
export const isPillarTableNestedTable = (
  child: React.ReactNode,
): child is PillarTableNestedTableElement => {
  if (!React.isValidElement(child)) {
    return false;
  }
  const componentType = child.type as { isNestedTable?: boolean };
  if (componentType?.isNestedTable) {
    return true;
  }
  const { columnType } = child.props as { columnType?: string };
  return columnType === ColumnTypes.NestedTable;
};

/** The nested tables declared on a table, in declaration order. */
export const filterPillarTableNestedTables = (
  columns: React.ReactNode[],
): PillarTableNestedTableElement[] => columns.filter(isPillarTableNestedTable);

/**
 * Everything that is an actual column. Nested tables live in the same children
 * list but render as their own full-width row, never as a cell.
 */
export const filterPillarTableDataColumns = (
  columns: React.ReactElement[],
): React.ReactElement[] =>
  columns.filter((column) => !isPillarTableNestedTable(column));

/** The rows one nested table shows for a parent row, or `[]` when it has none. */
export const getPillarTableNestedRows = (
  rowValue: PillarTableSafeAny,
  valuePath: PillarTableSafeAny,
): PillarTableSafeAny[] => {
  if (!rowValue || !valuePath) {
    return [];
  }
  const nestedRows = lodashGet(rowValue, valuePath as string);
  return Array.isArray(nestedRows) ? nestedRows : [];
};

/**
 * Whether a parent row has anything to expand into. A row is only togglable
 * when at least one of its nested tables has rows.
 */
export const hasPillarTableNestedRows = (
  rowValue: PillarTableSafeAny,
  nestedTables: PillarTableNestedTableElement[],
): boolean =>
  nestedTables.some(
    (nestedTable) =>
      getPillarTableNestedRows(rowValue, nestedTable.props.valuePath).length >
      0,
  );

/**
 * `handleRowClick` and a nested table both want the row click: one navigates,
 * the other expands. Configuring both is a mistake at the call site that no
 * runtime behaviour can resolve, so fail loudly instead of silently picking one.
 */
export const assertNoPillarTableRowClickWithNestedTable = ({
  testidTable,
  handleRowClick,
  columns,
}: {
  testidTable?: string;
  handleRowClick?: (row: PillarTableSafeAny) => void;
  columns: React.ReactElement[];
}) => {
  if (!handleRowClick) {
    return;
  }
  const nestedTables = filterPillarTableNestedTables(columns);
  if (!nestedTables.length) {
    return;
  }
  const valuePaths = nestedTables
    .map((nestedTable) => String(nestedTable.props.valuePath))
    .join(", ");
  throw new Error(
    `PillarTable "${testidTable ?? "unknown"}" configures both handleRowClick and PillarTable.NestedTable (${valuePaths}). ` +
      "A row click cannot navigate and expand a nested table at the same time. " +
      "Remove handleRowClick and put the navigation on a column with linkToAdmin/linkToHub, or remove the nested table.",
  );
};
